import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Convertir base64 a Uint8Array para la suscripción
function urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
        .replace(/-/g, '+')
        .replace(/_/g, '/');
    
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    
    for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
}

export function usePushNotifications() {
    const [isSupported, setIsSupported] = useState(false);
    const [isSubscribed, setIsSubscribed] = useState(false);
    const [subscription, setSubscription] = useState(null);
    const [permission, setPermission] = useState('default');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Verificar soporte
    useEffect(() => {
        const checkSupport = async () => {
            const supported = 'serviceWorker' in navigator && 'PushManager' in window;
            setIsSupported(supported);
            
            if (supported) {
                setPermission(Notification.permission);
                await checkSubscription();
            }
            setLoading(false);
        };
        
        checkSupport();
    }, []);

    // Verificar si ya hay suscripción
    const checkSubscription = async () => {
        try {
            const registration = await navigator.serviceWorker.ready;
            const existingSub = await registration.pushManager.getSubscription();
            
            if (existingSub) {
                setSubscription(existingSub);
                setIsSubscribed(true);
            }
        } catch (err) {
            console.error('Error checking subscription:', err);
        }
    };

    // Registrar Service Worker
    const registerServiceWorker = async () => {
        if (!('serviceWorker' in navigator)) {
            throw new Error('Service Worker no soportado');
        }
        
        const registration = await navigator.serviceWorker.register('/sw.js');
        await navigator.serviceWorker.ready;
        return registration;
    };

    // Suscribirse a notificaciones
    const subscribe = useCallback(async () => {
        if (!isSupported) {
            setError('Tu navegador no soporta notificaciones push');
            return false;
        }

        setLoading(true);
        setError(null);

        try {
            // Pedir permiso
            const permission = await Notification.requestPermission();
            setPermission(permission);
            
            if (permission !== 'granted') {
                setError('Permiso de notificaciones denegado');
                setLoading(false);
                return false;
            }

            // Registrar service worker
            const registration = await registerServiceWorker();
            
            // Obtener clave VAPID del servidor
            const token = localStorage.getItem('token');
            const vapidResponse = await axios.get(`${API_URL}/api/notifications/vapid-public-key`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            const vapidPublicKey = vapidResponse.data.publicKey;
            
            // Crear suscripción
            const pushSubscription = await registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
            });

            // Enviar suscripción al servidor
            await axios.post(
                `${API_URL}/api/notifications/subscribe`,
                {
                    endpoint: pushSubscription.endpoint,
                    keys: {
                        p256dh: btoa(String.fromCharCode(...new Uint8Array(pushSubscription.getKey('p256dh')))),
                        auth: btoa(String.fromCharCode(...new Uint8Array(pushSubscription.getKey('auth'))))
                    },
                    device_name: navigator.userAgent.includes('Mobile') ? 'Móvil' : 'Escritorio',
                    device_type: navigator.userAgent.includes('Mobile') ? 'mobile' : 'web'
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            setSubscription(pushSubscription);
            setIsSubscribed(true);
            setLoading(false);
            return true;
        } catch (err) {
            console.error('Error subscribing:', err);
            setError(err.message || 'Error al activar notificaciones');
            setLoading(false);
            return false;
        }
    }, [isSupported]);

    // Cancelar suscripción
    const unsubscribe = useCallback(async () => {
        if (!subscription) return false;
        
        setLoading(true);
        try {
            await subscription.unsubscribe();
            
            // Informar al servidor
            const token = localStorage.getItem('token');
            await axios.delete(`${API_URL}/api/notifications/unsubscribe`, {
                params: { endpoint: subscription.endpoint },
                headers: { Authorization: `Bearer ${token}` }
            });

            setSubscription(null);
            setIsSubscribed(false);
            setLoading(false);
            return true;
        } catch (err) {
            console.error('Error unsubscribing:', err);
            setError(err.message);
            setLoading(false);
            return false;
        }
    }, [subscription]);

    // Enviar notificación de prueba
    const sendTestNotification = useCallback(async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.post(
                `${API_URL}/api/notifications/send-test`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            return response.data;
        } catch (err) {
            setError(err.message);
            throw err;
        }
    }, []);

    return {
        isSupported,
        isSubscribed,
        permission,
        loading,
        error,
        subscribe,
        unsubscribe,
        sendTestNotification
    };
}

export default usePushNotifications;
